import { z } from "zod";

const dateTime = z.string().datetime({ offset: true });
const optionalDateTime = dateTime.or(z.literal(""));
const taskStatus = z.enum([
  "draft", "approval_required", "scheduled", "active", "processing",
  "matched", "executed", "canceled", "expired", "failed",
]);
const freshPlan = z.object({
  profile_id: z.string().trim().min(1).max(128),
  targets: z.array(z.object({
    subject: z.string().trim().min(1).max(512),
    target_type: z.string().max(128),
    platform: z.string().max(128),
    country_context: z.string().max(128),
  }).strict()).min(1).max(8),
  requested_modules: z.array(z.string().trim().min(1).max(128)).min(1).max(30),
  max_external_requests: z.number().int().min(0).max(1000),
  cost_estimate_available: z.literal(false),
  source_choice: z.object({
    capability_id: z.string().trim().min(1).max(128),
    platform: z.string().trim().min(1).max(128),
    stability: z.enum(["STABLE", "DEGRADED", "INSUFFICIENT_RECENT_EVIDENCE"]),
    decision_fields: z.array(z.string().trim().min(1).max(128)).min(1).max(8),
  }).strict().optional(),
}).strict();
const cwmTaskWire = z.object({
  id: z.string().trim().min(1).max(160),
  provider: z.literal("cwm_private_gateway"),
  condition_schema: z.string().trim().max(128),
  task_type: z.string().trim().max(128),
  status: taskStatus,
  cancel_pending: z.boolean().optional(),
  cancel_request_unknown: z.boolean().optional(),
  cancel_requested_at: optionalDateTime.optional(),
  last_result: z.unknown(),
  fresh_plan: freshPlan.nullable(),
  next_check_at: optionalDateTime,
  expires_at: optionalDateTime,
  failure_count: z.number().int().nonnegative(),
  last_error_code: z.string().max(128),
  last_error_message: z.literal(""),
  created_at: dateTime,
  updated_at: dateTime,
}).strict().refine(
  (task) => !(task.cancel_pending === true && task.cancel_request_unknown === true),
  "现采取消待核对与已接收状态不能同时成立",
);

const taskEnvelope = z.object({ tasks: z.array(z.unknown()) }).strict();
const providerTag = z.object({ provider: z.string() }).passthrough();
const cwmKindTag = z.object({
  provider: z.literal("cwm_private_gateway"),
  condition_schema: z.string(),
}).passthrough();
const lastResult = z.object({
  ok: z.boolean().optional(),
  pending: z.boolean().optional(),
  phase: z.string().max(128).optional(),
  completion_state: z.enum(["COMPLETE", "PARTIAL", "VALID_EMPTY", "FAILED"]).optional(),
  notification_pending: z.boolean().optional(),
  result: z.object({ knowledge_cutoff: dateTime.optional() }).strict().optional(),
  error: z.object({ code: z.string().max(128).optional() }).strict().optional(),
}).strict();

export interface ConversationFreshTask {
  id: string;
  status: z.infer<typeof taskStatus>;
  plan: z.infer<typeof freshPlan> | null;
  phase: string;
  completionState: "COMPLETE" | "PARTIAL" | "VALID_EMPTY" | "FAILED" | null;
  notificationPending: boolean;
  pending: boolean;
  cancelPending: boolean;
  cancelRequestUnknown: boolean;
  cancelRequestedAt: string | null;
  retryableReadError: boolean;
  errorCode: string;
  knowledgeCutoff: string | null;
  createdAt: string;
  expiresAt: string | null;
  updatedAt: string;
}

export function parseConversationFreshTasks(input: unknown): ConversationFreshTask[] {
  const envelope = taskEnvelope.parse(input);
  return envelope.tasks.flatMap((raw) => {
    const tag = providerTag.safeParse(raw);
    if (!tag.success || tag.data.provider !== "cwm_private_gateway") return [];
    const kind = cwmKindTag.parse(raw);
    if (kind.condition_schema !== "cwm_fresh_collection.v1") return [];
    const task = cwmTaskWire.parse(raw);
    const result = lastResult.parse(task.last_result);
    return [{
      id: task.id,
      status: task.status,
      plan: task.fresh_plan,
      phase: result.phase ?? "",
      completionState: result.completion_state ?? null,
      notificationPending: result.notification_pending ?? false,
      pending: result.pending ?? false,
      cancelPending: task.cancel_pending ?? false,
      cancelRequestUnknown: task.cancel_request_unknown ?? false,
      cancelRequestedAt: task.cancel_requested_at || null,
      retryableReadError: result.ok === false && result.pending === true,
      errorCode: task.last_error_code || result.error?.code || "",
      knowledgeCutoff: result.result?.knowledge_cutoff ?? null,
      createdAt: task.created_at,
      expiresAt: task.expires_at || null,
      updatedAt: task.updated_at,
    }];
  });
}

export function freshTaskStatusLabel(task: ConversationFreshTask) {
  if (task.cancelPending && isFreshTaskActive(task)) return "取消请求已接收，等待现采终态确认";
  if (task.cancelRequestUnknown && isFreshTaskActive(task)) return "取消请求状态待核对，尚未证明已接收";
  if (task.notificationPending) return "结果已出，正在同步到对话";
  if (task.retryableReadError) return "暂时无法读取，正在重试";
  switch (task.status) {
    case "approval_required": return "等待确认现采范围";
    case "draft": return "现采计划待确认";
    case "scheduled": return "现采已排队";
    case "active":
    case "processing": return "正在采集";
    case "matched":
      if (task.completionState === "PARTIAL") return "现采部分完成";
      if (task.completionState === "VALID_EMPTY") return "现采完成，当前无符合条件的数据";
      return "现采完成";
    case "canceled": return "现采已取消";
    case "expired": return "现采等待超时";
    case "failed": return "现采失败，未取得可用结果";
    case "executed": return "现采已结束";
  }
}

export function isFreshTaskActive(task: ConversationFreshTask) {
  return ["draft", "approval_required", "scheduled", "active", "processing"].includes(task.status);
}
